import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Space,
  Switch,
  Table,
  Tag,
  message,
} from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type Stop = { id: string; code: string; name: string; sequence_no: number };
type RouteDetail = {
  route: {
    id: string;
    code: string;
    name: string;
    capacity?: number | null;
    vehicle_label?: string | null;
    is_active?: number | boolean;
  };
  stops: Stop[];
  active_riders: number;
};

type RouteForm = {
  code: string;
  name: string;
  capacity?: number | null;
  vehicle_label?: string;
  is_active?: boolean;
};

type StopForm = { code: string; name: string; sequence_no?: number };

export default function TransportRouteDetailPage() {
  const { t } = useTranslation('education');
  const { t: tc } = useTranslation('common');
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.transport.manage') ||
      user?.permissions?.includes('education.admin')
  );

  const [editOpen, setEditOpen] = useState(false);
  const [addStopOpen, setAddStopOpen] = useState(false);
  const [editingStop, setEditingStop] = useState<Stop | null>(null);
  const [routeForm] = Form.useForm<RouteForm>();
  const [stopForm] = Form.useForm<StopForm>();
  const [editStopForm] = Form.useForm<StopForm>();

  const query = useQuery({
    queryKey: ['education-transport-route', id],
    enabled: Boolean(id),
    queryFn: () => api.get<RouteDetail>(`/education/transport/routes/${id}`),
  });
  const detail = query.data?.data;
  const route = detail?.route;

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['education-transport-route', id] });
    queryClient.invalidateQueries({ queryKey: ['education-transport-routes'] });
  };

  const updateRoute = useMutation({
    mutationFn: (values: RouteForm) => api.put(`/education/transport/routes/${id}`, values),
    onSuccess: () => {
      message.success(t('transportRouteUpdated'));
      setEditOpen(false);
      refresh();
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const addStop = useMutation({
    mutationFn: (values: StopForm) => api.post(`/education/transport/routes/${id}/stops`, values),
    onSuccess: () => {
      message.success(t('transportStopAdded'));
      setAddStopOpen(false);
      stopForm.resetFields();
      refresh();
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const updateStop = useMutation({
    mutationFn: (values: StopForm & { stopId: string }) =>
      api.put(`/education/transport/routes/${id}/stops/${values.stopId}`, {
        code: values.code,
        name: values.name,
        sequence_no: values.sequence_no,
      }),
    onSuccess: () => {
      message.success(t('transportStopUpdated'));
      setEditingStop(null);
      refresh();
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const deleteStop = useMutation({
    mutationFn: (stopId: string) => api.delete(`/education/transport/routes/${id}/stops/${stopId}`),
    onSuccess: () => {
      message.success(t('transportStopDeleted'));
      refresh();
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  return (
    <EducationPageChrome
      title={route ? route.name : t('transportRoutes')}
      description={route ? route.code : t('transportRoutesIntro')}
      tags={
        route ? (
          <Tag color={route.is_active === 0 || route.is_active === false ? undefined : 'success'}>
            {route.is_active === 0 || route.is_active === false ? t('inactive') : t('active')}
          </Tag>
        ) : undefined
      }
      extra={
        <Space wrap>
          {canManage && route ? (
            <>
              <Button
                onClick={() => {
                  routeForm.setFieldsValue({
                    code: route.code,
                    name: route.name,
                    capacity: route.capacity ?? undefined,
                    vehicle_label: route.vehicle_label || undefined,
                    is_active: !(route.is_active === 0 || route.is_active === false),
                  });
                  setEditOpen(true);
                }}
              >
                {t('editTransportRoute')}
              </Button>
              <Button
                type="primary"
                onClick={() => {
                  stopForm.setFieldsValue({ sequence_no: (detail?.stops?.length ?? 0) + 1 });
                  setAddStopOpen(true);
                }}
              >
                {t('addTransportStop')}
              </Button>
            </>
          ) : null}
          <Button onClick={() => navigate('/education/transport/routes')}>{t('backToTransportRoutes')}</Button>
        </Space>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Card size="small" loading={query.isLoading}>
          {route ? (
            <Space wrap size="large">
              <span>
                <strong>{t('capacity')}:</strong> {route.capacity ?? '—'}
              </span>
              <span>
                <strong>{t('vehicleLabel')}:</strong> {route.vehicle_label || '—'}
              </span>
              <span>
                <strong>{t('activeRiders')}:</strong> {detail?.active_riders ?? 0}
              </span>
            </Space>
          ) : null}
        </Card>

        <Card size="small" title={t('stops')}>
          <Table
            rowKey="id"
            size="small"
            pagination={false}
            loading={query.isLoading}
            dataSource={detail?.stops ?? []}
            columns={[
              { title: t('sequence'), dataIndex: 'sequence_no', width: 90 },
              { title: t('code'), dataIndex: 'code', width: 100 },
              { title: t('name'), dataIndex: 'name' },
              ...(canManage
                ? [
                    {
                      title: t('actions'),
                      width: 180,
                      render: (_: unknown, row: Stop) => (
                        <Space>
                          <Button
                            size="small"
                            onClick={() => {
                              setEditingStop(row);
                              editStopForm.setFieldsValue(row);
                            }}
                          >
                            {tc('edit')}
                          </Button>
                          <Popconfirm
                            title={t('transportStopDeleteConfirm')}
                            onConfirm={() => deleteStop.mutate(row.id)}
                          >
                            <Button size="small" danger loading={deleteStop.isPending}>
                              {tc('delete')}
                            </Button>
                          </Popconfirm>
                        </Space>
                      ),
                    },
                  ]
                : []),
            ]}
          />
        </Card>
      </Space>

      <Modal title={t('editTransportRoute')} open={editOpen} onCancel={() => setEditOpen(false)} footer={null} destroyOnClose>
        <Form form={routeForm} layout="vertical" onFinish={(values) => updateRoute.mutate(values)}>
          <Form.Item name="code" label={t('code')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="name" label={t('name')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="capacity" label={t('capacity')}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="vehicle_label" label={t('vehicleLabel')}>
            <Input />
          </Form.Item>
          <Form.Item name="is_active" label={t('active')} valuePropName="checked">
            <Switch />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={updateRoute.isPending} block>
            {t('save')}
          </Button>
        </Form>
      </Modal>

      <Modal title={t('addTransportStop')} open={addStopOpen} onCancel={() => setAddStopOpen(false)} footer={null} destroyOnClose>
        <Form form={stopForm} layout="vertical" onFinish={(values) => addStop.mutate(values)}>
          <Form.Item name="code" label={t('code')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="name" label={t('name')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="sequence_no" label={t('sequence')}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={addStop.isPending} block>
            {t('save')}
          </Button>
        </Form>
      </Modal>

      <Modal
        title={t('editTransportStop')}
        open={Boolean(editingStop)}
        onCancel={() => setEditingStop(null)}
        footer={null}
        destroyOnClose
      >
        <Form
          form={editStopForm}
          layout="vertical"
          onFinish={(values) => editingStop && updateStop.mutate({ ...values, stopId: editingStop.id })}
        >
          <Form.Item name="code" label={t('code')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="name" label={t('name')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="sequence_no" label={t('sequence')}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={updateStop.isPending} block>
            {t('save')}
          </Button>
        </Form>
      </Modal>
    </EducationPageChrome>
  );
}
